import countries from '../../data/countries.json' with { type: 'json' };
import { countryNameToCode } from '../../utils/country-code-mapping';

export type AnalyticsCountryProperties = {
  country: string | undefined;
  country_name: string | undefined;
  country_code: string | undefined;
  country_source: 'account_profile' | undefined;
};

const countryCodesByName = new Map([...Object.entries(countryNameToCode), ['eswatini', 'SZ']]);
const countryNamesByCode = new Map(
  countries.map(({ value, label }) => [countryCodesByName.get(value), label]),
);

const countryAliases = new Map([
  ['uk', 'GB'],
  ['u-k', 'GB'],
  ['great-britain', 'GB'],
  ['britain', 'GB'],
  ['usa', 'US'],
  ['u-s', 'US'],
  ['u-s-a', 'US'],
  ['united-states-of-america', 'US'],
  ['uae', 'AE'],
  ['u-a-e', 'AE'],
]);

const getCountryName = (code: string): string | undefined => {
  const knownName = countryNamesByCode.get(code);
  if (knownName) return knownName;

  // Detected account countries can include territories absent from the signup list.
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' }).of(code);
    return name && name !== code && name !== 'Unknown Region' ? name : undefined;
  } catch {
    return undefined;
  }
};

/** Human-readable account country; this does not describe the device's location. */
export const getAnalyticsCountryProperties = (country: unknown): AnalyticsCountryProperties => {
  const unavailable: AnalyticsCountryProperties = {
    country: undefined,
    country_name: undefined,
    country_code: undefined,
    country_source: undefined,
  };

  if (typeof country !== 'string' || !country.trim()) return unavailable;

  const normalized = country.trim().toLowerCase().replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const code = countryAliases.get(normalized) || countryCodesByName.get(normalized)
    || (/^[a-z]{2}$/.test(normalized) ? normalized.toUpperCase() : undefined);
  if (!code) return unavailable;

  // Intl also recognizes these non-country region identifiers.
  if (['EU', 'EZ', 'UN', 'ZZ', 'XA', 'XB'].includes(code)) return unavailable;

  const name = getCountryName(code);
  if (!name) return unavailable;

  return {
    country: name,
    country_name: name,
    country_code: code,
    country_source: 'account_profile',
  };
};
